import React, { useEffect, useRef, useState } from 'react';
import { Box, Paper } from '@mui/material';

// Simple diff implementation to avoid external dependency
const diffWords = (oldStr: string, newStr: string) => {
  const oldWords = oldStr.split(/\s+/);
  const newWords = newStr.split(/\s+/);
  const result = [];
  
  let i = 0, j = 0;
  while (i < oldWords.length || j < newWords.length) {
    if (i >= oldWords.length) {
      result.push({ added: true, value: newWords[j] + ' ' });
      j++;
    } else if (j >= newWords.length) {
      result.push({ removed: true, value: oldWords[i] + ' ' });
      i++;
    } else if (oldWords[i] === newWords[j]) {
      result.push({ value: oldWords[i] + ' ' });
      i++;
      j++;
    } else {
      // Simple approach: mark as removed then added
      result.push({ removed: true, value: oldWords[i] + ' ' });
      result.push({ added: true, value: newWords[j] + ' ' });
      i++;
      j++;
    }
  }
  
  return result;
};

interface ContentEditorProps {
  id?: string;
  content: string;
  onChange: (content: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  highlights?: string[];
  showDiff?: boolean;
  originalContent?: string;
}

export const ContentEditor: React.FC<ContentEditorProps> = ({
  id,
  content,
  onChange,
  placeholder = 'Enter content...',
  readOnly = false,
  highlights = [],
  showDiff = false,
  originalContent = ''
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [localContent, setLocalContent] = useState(content);
  const [isEditing, setIsEditing] = useState(false);

  // Update local content when prop changes
  useEffect(() => {
    if (!isEditing) {
      setLocalContent(content);
    }
  }, [content, isEditing]);

  // Render the content with proper HTML
  const renderContent = () => {
    if (!localContent && !readOnly) {
      return `<p style="color: #999; font-style: italic;">${placeholder}</p>`;
    }

    if (showDiff && originalContent) {
      return renderDiff();
    }

    // Check if content is HTML
    const isHtml = localContent.includes('<') && localContent.includes('>');
    
    if (isHtml) {
      // Apply highlights to HTML content if needed
      let processedContent = localContent;
      if (highlights.length > 0) {
        highlights.forEach(term => {
          const regex = new RegExp(`(${term})`, 'gi');
          processedContent = processedContent.replace(regex, '<mark class="highlight-question">$1</mark>');
        });
      }
      return processedContent;
    } else {
      // Plain text - convert to HTML for display
      let processedContent = localContent
        .split('\n\n')
        .map(para => `<p>${para}</p>`)
        .join('');
      
      // Apply highlights
      if (highlights.length > 0) {
        highlights.forEach(term => {
          const regex = new RegExp(`(${term})`, 'gi');
          processedContent = processedContent.replace(regex, '<mark class="highlight-question">$1</mark>');
        });
      }
      
      return processedContent;
    }
  };

  // Show diff between original and optimized
  const renderDiff = () => {
    const diff = diffWords(originalContent, localContent);
    let html = '';
    
    diff.forEach(part => {
      if (part.added) {
        html += `<span class="diff-added">${part.value}</span>`;
      } else if (part.removed) {
        html += `<span class="diff-removed">${part.value}</span>`;
      } else {
        html += part.value;
      }
    });
    
    return html;
  };

  const handleInput = () => {
    if (editorRef.current && !readOnly) {
      setIsEditing(true);
      const text = editorRef.current.innerText || '';
      setLocalContent(text);
      onChange(text);
    }
  };

  const handleBlur = () => {
    setIsEditing(false);
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    if (readOnly) return;
    
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
    handleInput();
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        minHeight: 400,
        maxHeight: 600,
        overflow: 'auto',
        backgroundColor: readOnly ? '#f5f5f5' : '#fff'
      }}
    >
      <Box
        ref={editorRef}
        id={id}
        contentEditable={!readOnly}
        onInput={handleInput}
        onBlur={handleBlur}
        onPaste={handlePaste}
        dangerouslySetInnerHTML={{ __html: renderContent() }}
        sx={{
          minHeight: '100%',
          outline: 'none',
          fontFamily: 'Georgia, serif',
          fontSize: '16px',
          lineHeight: 1.8,
          color: '#333',
          '& .highlight-question': {
            backgroundColor: '#ffeb3b',
            padding: '2px 4px',
            borderRadius: '3px',
          },
          '& .diff-added': {
            backgroundColor: '#c8f7c5',
            textDecoration: 'none',
          },
          '& .diff-removed': {
            backgroundColor: '#ffcdd2',
            textDecoration: 'line-through',
            opacity: 0.7,
          },
          '& mark': {
            backgroundColor: '#fff59d',
            padding: '0 2px',
          },
          '& h1': {
            fontSize: '2em',
            fontWeight: 'bold',
            marginTop: '0.67em',
            marginBottom: '0.67em',
          },
          '& h2': {
            fontSize: '1.5em',
            fontWeight: 'bold',
            marginTop: '0.83em',
            marginBottom: '0.83em',
          },
          '& h3': {
            fontSize: '1.17em',
            fontWeight: 'bold',
            marginTop: '1em',
            marginBottom: '1em',
          },
          '& p': {
            marginBottom: '1em',
          },
          '& ul, & ol': {
            marginLeft: '2em',
            marginBottom: '1em',
          },
          '& li': {
            marginBottom: '0.5em',
          },
          '& img': {
            maxWidth: '100%',
            height: 'auto',
            display: 'block',
            margin: '1em 0',
          },
          '& a': {
            color: '#1976d2',
            textDecoration: 'underline',
          },
          '& blockquote': {
            borderLeft: '4px solid #ddd',
            paddingLeft: '1em',
            marginLeft: 0,
            fontStyle: 'italic',
            color: '#666',
          },
          '& code': {
            backgroundColor: '#f5f5f5',
            padding: '2px 4px',
            borderRadius: '3px',
            fontFamily: 'monospace',
          },
          '& pre': {
            backgroundColor: '#f5f5f5',
            padding: '1em',
            borderRadius: '4px',
            overflow: 'auto',
          },
          '& table': {
            borderCollapse: 'collapse',
            width: '100%',
            marginBottom: '1em',
          },
          '& th, & td': {
            border: '1px solid #ddd',
            padding: '8px',
            textAlign: 'left',
          },
          '& th': {
            backgroundColor: '#f5f5f5',
            fontWeight: 'bold',
          }
        }}
      />
    </Paper>
  );
};